import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Siren } from 'lucide-react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { ApiError, api } from '../api/client';
import { useRole } from '../context/RoleContext';
import type { Atelier, CascadeEtape, ContactUrgence, ContexteUrgence, Demande, FakKit, Personne, Pole, ResultatAppel, SeanceProgramme } from '../types';
import { dansNJoursISO, todayISO } from '../components/annuaire/format';
import { FicheFakOverlay } from '../components/fak/FicheFakOverlay';
import { SmsAEnvoyer } from '../components/messages/SmsAEnvoyer';
import { DeclarerCarte, UrgencesEnCours } from '../components/urgence/ColonneGauche';
import { ModalAnnulation, ModalAutreCas, ModalDeclarer, ModalFusion, ModalMaintien } from '../components/urgence/FenetresUrgence';
import { TableauUrgence } from '../components/urgence/TableauUrgence';
import { LIBELLE_COURT, accepteEnAttente, corrigerAppel, etapeCourante, noterAppel, passerALaSuivante, prochainContact, resumeAppel } from '../components/urgence/regles';

// Console Urgence (DECISIONS §29) : à gauche, déclarer (par nom) puis les urgences en cours ; à droite, UNE urgence au travail — le prochain contact à appeler,
// les contacts avec leur disponibilité, où en est la cascade, le journal. L'écran se rafraîchit seul toutes les 30 s et dit si quelqu'un d'autre a modifié l'urgence.

const FENETRE_JOURS = 7;
const SEUIL_DEFAUT_MINUTES = 60;
const PERIODE_ACTUALISATION_MS = 30000;

type Fenetre = null | 'fusion' | 'maintien' | 'annulation' | 'autre' | { declarer: SeanceProgramme };

const heure = (ms: number) => new Date(ms).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }).replace(/^0/, '').replace(':', ' h ').replace(/ h 00$/, ' h');
const jourLocal = (iso: string) => { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/** Met le focus sur l'élément et, si ce qu'il montre n'est pas à l'écran (ou passe sous la barre du haut), le ramène au milieu : le geste a pu se faire loin d'où la suite se passe. */
function montrer(el: HTMLElement) {
  el.focus({ preventScroll: true });
  const r = (el.closest('[role="region"]') ?? el).getBoundingClientRect();
  if (r.top < 72 || r.bottom > window.innerHeight - 16) el.scrollIntoView({ block: 'center' });
}

export function UrgencePage({ param }: { param?: number | null } = {}) {
  const { peutGerer } = useRole();
  const { user } = useAuth();
  const moi = user?.name ?? 'Quelqu’un';

  // Référentiels
  const [poles, setPoles] = useState<Pole[]>([]);
  const [personnes, setPersonnes] = useState<Personne[]>([]);
  const [ateliers, setAteliers] = useState<Atelier[]>([]);
  const [seances, setSeances] = useState<SeanceProgramme[]>([]);
  const [seuilMinutes, setSeuilMinutes] = useState(SEUIL_DEFAUT_MINUTES);

  // Les demandes de la semaine (pour dire si une séance est déjà prise en charge), et les urgences en cours
  const [fenetreDemandes, setFenetreDemandes] = useState<Demande[]>([]);
  const [ouvertes, setOuvertes] = useState<Demande[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [majA, setMajA] = useState(() => Date.now());
  const [versionSms, setVersionSms] = useState(0);
  const [annonce, setAnnonce] = useState('');

  // L'urgence au travail
  const [selectedId, setSelectedId] = useState<number | null>(param ?? null);
  const [selected, setSelected] = useState<Demande | null>(null);
  const [selectedAtelier, setSelectedAtelier] = useState<Atelier | null>(null);
  const [fak, setFak] = useState<FakKit | null>(null);
  const [contexte, setContexte] = useState<ContexteUrgence | null>(null);
  const [contexteIndisponible, setContexteIndisponible] = useState(false);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<{ id: number; prevenus: string[] } | null>(null);
  const [distant, setDistant] = useState<{ par: string | null } | null>(null);
  /** Un geste refusé parce que quelqu'un d'autre avait modifié l'urgence : on le dit (l'écran, lui, est remis à jour tout seul). */
  const [conflit, setConflit] = useState<string | null>(null);
  const [fenetre, setFenetre] = useState<Fenetre>(null);
  const [ficheFakOuverte, setFicheFakOuverte] = useState(false);
  const [occupe, setOccupe] = useState(false);

  // ── Ce que l'écran a vu de l'urgence au travail (pour ne jamais écraser le travail d'une autre personne) ──────────────────
  const versionRef = useRef<string | null>(null);
  const etapesRef = useRef<CascadeEtape[] | null>(null);
  const selectedIdRef = useRef<number | null>(selectedId);
  selectedIdRef.current = selectedId;
  const enAttente = useRef(0);                     // enregistrements en cours : pas de comparaison avec le serveur pendant ce temps
  const file = useRef<Promise<void>>(Promise.resolve());
  const aFocaliser = useRef(false);
  const focusApres = useRef<string | null>(null);   // où poser le focus une fois le geste enregistré (le bouton cliqué n'existe plus)

  // ── Chargements ──────────────────────────────────────────────────────────────────────────────────────────────────────
  const chargerFenetre = useCallback(async () => {
    const [du, au] = [todayISO(), dansNJoursISO(FENETRE_JOURS - 1)];
    const [seancesRes, demandesRes] = await Promise.all([api.getSeances({ du, au }), api.getDemandes({ du, au })]);
    setSeances(seancesRes); setFenetreDemandes(demandesRes);
  }, []);

  const rafraichirListe = useCallback(async () => {
    try {
      const liste = await api.getDemandes({ statut: 'urgence_cascade' });
      setOuvertes(liste); setMajA(Date.now()); setNow(Date.now());
      const id = selectedIdRef.current;
      if (id != null && enAttente.current === 0 && versionRef.current != null) {
        const courante = liste.find((d) => d.id === id);
        if (!courante) setDistant((d) => d ?? { par: null });   // terminée par quelqu'un d'autre
        else if (Math.abs(new Date(courante.updated_at).getTime() - new Date(versionRef.current).getTime()) > 2) setDistant({ par: courante.derniere_action?.par_nom ?? null });
      }
    } catch { /* le prochain tour réessaiera : la console reste utilisable */ }
  }, []);

  const chargerSelection = useCallback(async (id: number, silencieux = false) => {
    if (!silencieux) setSelectedLoading(true);
    try {
      const [d, ctx] = await Promise.all([api.getDemande(id), api.getContexteUrgence(id).then((c) => ({ c }), () => ({ c: null }))]);
      if (selectedIdRef.current !== id) return;
      const [atelier, fakDetail] = await Promise.all([api.getAtelier(d.atelier_id), api.getFak(d.atelier_id).catch(() => null)]);
      if (selectedIdRef.current !== id) return;
      setSelected(d); etapesRef.current = d.cascade_etapes; versionRef.current = d.updated_at;
      setSelectedAtelier(atelier); setFak(fakDetail?.fak ?? null);
      setContexte(ctx.c); setContexteIndisponible(ctx.c == null);
      setDistant(null);
    } catch (err) {
      if (selectedIdRef.current === id) setError(err instanceof Error ? err.message : "Échec du chargement de l'urgence");
    } finally { setSelectedLoading(false); }
  }, []);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    Promise.all([api.getMeta(), api.getPersonnes({ actif: true }), api.getAteliers({ actif: true, reel: true }), api.getDemandes({ statut: 'urgence_cascade' }), api.getSettings().catch(() => ({}) as Record<string, string>), chargerFenetre()])
      .then(([meta, pers, ats, liste, settings]) => {
        if (annule) return;
        setPoles(meta.poles); setPersonnes(pers); setAteliers(ats); setOuvertes(liste);
        const seuil = Number(settings['urgence.decision_management_minutes']);
        if (!Number.isNaN(seuil) && seuil > 0) setSeuilMinutes(seuil);
        // Ouverte sans urgence choisie : la plus proche, pour qu'on arrive directement sur le travail.
        if (selectedIdRef.current == null && liste.length > 0) {
          const ordre = [...liste].sort((a, b) => String(a.date_atelier).localeCompare(String(b.date_atelier)) || (a.atelier_heure_debut ?? '').localeCompare(b.atelier_heure_debut ?? ''));
          setSelectedId(ordre[0].id);
        }
      })
      .catch((err) => { if (!annule) setError(err instanceof Error ? err.message : 'Échec du chargement'); })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
  }, [chargerFenetre]);

  // Ouverte sur une urgence (#urgence/ID : lien de la notification, bouton de la fiche).
  useEffect(() => { if (param != null) { aFocaliser.current = true; setSelectedId(param); } }, [param]);
  useEffect(() => {
    if (selectedId == null) { setSelected(null); setContexte(null); etapesRef.current = null; versionRef.current = null; return; }
    void chargerSelection(selectedId);
  }, [selectedId, chargerSelection]);
  // Le détail s'ouvre sous les yeux : le focus entre sur son titre quand l'urgence vient d'être déclarée ou choisie.
  useEffect(() => {
    if (!aFocaliser.current || selectedLoading || !selected || selected.id !== selectedId) return;
    aFocaliser.current = false;
    const t = document.getElementById('u-board-t');
    t?.focus(); t?.scrollIntoView({ block: 'nearest' });
  }, [selected, selectedLoading, selectedId]);

  // Un geste vient d'être enregistré : le focus va où la suite se passe (panneau de confirmation, prochain contact, ligne cliquée), et l'écran suit si besoin.
  useEffect(() => {
    const id = focusApres.current;
    if (!id || !selected) return;
    focusApres.current = null;
    const el = document.getElementById(id);
    if (el) montrer(el);
  }, [selected]);

  // Actualisation automatique : même rythme que la cloche.
  useEffect(() => {
    const t = setInterval(() => { setNow(Date.now()); void rafraichirListe(); }, PERIODE_ACTUALISATION_MS);
    return () => clearInterval(t);
  }, [rafraichirListe]);

  const actualiser = useCallback(async () => {
    await Promise.all([rafraichirListe(), chargerFenetre().catch(() => {}), selectedIdRef.current != null ? chargerSelection(selectedIdRef.current, true) : Promise.resolve()]);
    setAnnonce('Mis à jour.');
  }, [rafraichirListe, chargerFenetre, chargerSelection]);

  /** Le bouton « Actualiser » : la personne a vu l'avertissement, on l'efface. */
  const actualiserManuel = () => { setConflit(null); void actualiser(); };

  // ── Enregistrer l'état de la cascade : un enregistrement à la fois, chacun calculé sur la liste à jour ───────────────────
  const enregistrer = useCallback((modifier: (etapes: CascadeEtape[]) => CascadeEtape[] | null, resume: string, apres?: (maj: Demande) => string | null, focus?: string) => {
    const id = selectedIdRef.current;
    if (id == null) return;
    enAttente.current += 1;
    file.current = file.current.then(async () => {
      try {
        const base = etapesRef.current;
        if (!base) return;
        const etapes = modifier(base);
        if (!etapes) return;
        const maj = await api.updateCascade(id, etapes, { version: versionRef.current ?? undefined, resume });
        etapesRef.current = maj.cascade_etapes ?? etapes; versionRef.current = maj.updated_at;
        setSelected((s) => (s && s.id === maj.id ? { ...maj, declaree: s.declaree, derniere_action: s.derniere_action } : s));
        setOuvertes((prev) => prev.map((d) => (d.id === maj.id ? { ...maj, declaree: d.declaree, derniere_action: { par_nom: moi, at: new Date().toISOString() } } : d)));
        setConfirmation(null); setConflit(null);
        if (focus) focusApres.current = focus;
        const texte = apres?.(maj);
        if (texte) setAnnonce(texte);
        api.getContexteUrgence(id).then((c) => { if (selectedIdRef.current === id) setContexte(c); }).catch(() => {});
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && err.corps.conflit) {
          setConflit("Quelqu'un vient de modifier cette urgence : votre dernier geste n'a pas été enregistré. L'écran est à jour : vérifiez, puis recommencez.");
          void actualiser(); setError(null);
        }
        else setError(err instanceof Error ? err.message : 'Échec de la mise à jour de la cascade');
      } finally { enAttente.current -= 1; }
    });
  }, [moi, actualiser]);

  const surAppel = (cle: string, c: ContactUrgence, r: ResultatAppel) => {
    enregistrer((base) => noterAppel(base, cle, c.personne_id, r, moi, new Date().toISOString()), resumeAppel(c.display_name, r), (maj) => {
      if (r === 'accepte') return `${c.display_name} accepte. Confirmez le remplacement ou revenez en arrière.`;
      const p = prochainContact(maj.cascade_etapes ?? [], contexte?.contacts ?? null);
      return `${resumeAppel(c.display_name, r)}. ${p?.contact ? `Prochain contact : ${p.contact.display_name}.` : 'Tous les contacts disponibles de cette étape ont été appelés.'}`;
    }, r === 'accepte' ? 'u-conf-t' : `u-corr-${cle}-${c.personne_id}`);
  };
  const surCorriger = (cle: string, personneId: number) => {
    const nom = personnes.find((p) => p.id === personneId)?.display_name ?? 'Contact';
    const etaitAccepte = accepteEnAttente(etapesRef.current ?? [])?.personneId === personneId;
    enregistrer((base) => corrigerAppel(base, cle, personneId), `${nom} : appel corrigé`, undefined, etaitAccepte ? 'u-pr-t' : `u-res-${cle}-${personneId}`);
  };
  const surPasser = (statut: 'echec' | 'saute') => {
    const etapes = etapesRef.current ?? [];
    const i = etapeCourante(etapes);
    if (i === -1) return;
    const nom = LIBELLE_COURT[etapes[i].cle] ?? etapes[i].libelle;
    enregistrer((base) => passerALaSuivante(base, new Date().toISOString(), moi, statut), `Étape « ${nom} » : ${statut === 'saute' ? 'sautée' : 'échec'}`, (maj) => {
      const suivante = (maj.cascade_etapes ?? [])[etapeCourante(maj.cascade_etapes ?? [])];
      return suivante ? `Étape suivante : ${LIBELLE_COURT[suivante.cle] ?? suivante.libelle}.` : null;
    }, 'u-pr-t');
  };
  const surCommentaire = (cle: string, texte: string) => {
    const nom = LIBELLE_COURT[cle] ?? cle;
    enregistrer((base) => base.map((e) => (e.cle === cle ? { ...e, commentaire: texte } : e)), `Étape « ${nom} » : ${texte.trim() ? 'note ajoutée' : 'note retirée'}`);
  };

  // ── Déclarer, et terminer ────────────────────────────────────────────────────────────────────────────────────────────
  const majDemande = (d: Demande) => {
    setFenetreDemandes((prev) => [d, ...prev.filter((x) => x.id !== d.id)]);
    setOuvertes((prev) => (d.statut === 'urgence_cascade' ? [d, ...prev.filter((x) => x.id !== d.id)] : prev.filter((x) => x.id !== d.id)));
  };
  const surDeclaree = (created: Demande) => {
    majDemande(created);
    setFenetre(null);
    aFocaliser.current = true;
    setConfirmation({ id: created.id, prevenus: created.responsables_prevenus ?? [] });
    setSelectedId(created.id);
    setAnnonce(`Urgence déclarée pour ${created.absent_nom ?? 'la personne absente'} : ${created.atelier_nom ?? ''}.`);
    void rafraichirListe();
  };
  const surIssue = (updated: Demande) => {
    setFenetre(null);
    majDemande(updated);
    setVersionSms((v) => v + 1);
    setConfirmation(null);
    aFocaliser.current = true;
    void chargerSelection(updated.id, true);
    void rafraichirListe();
    setAnnonce(updated.statut === 'maintenue' ? 'Atelier maintenu.' : updated.statut === 'annulee' ? 'Atelier annulé : les SMS sont prêts à envoyer.' : 'Atelier fusionné.');
  };
  const confirmerRemplacement = async (personneId: number) => {
    if (!selected) return;
    setOccupe(true);
    try {
      await file.current;   // les enregistrements en cours d'abord
      surIssue(await api.appliquerAction(selected.id, 'urgence_maintien', { remplacant_personne_id: personneId }));
    } catch (err) { setError(err instanceof Error ? err.message : 'Échec du maintien'); }
    finally { setOccupe(false); }
  };

  const choisir = (id: number) => { setConfirmation(null); setConflit(null); aFocaliser.current = true; setSelectedId(id); };
  const poleDe = (a: Atelier | undefined) => poles.find((p) => p.id === a?.pole_id) ?? null;
  const atelierParId = useMemo(() => new Map(ateliers.map((a) => [a.id, a])), [ateliers]);

  // Les urgences terminées aujourd'hui (par n'importe qui) : retrouvables, jamais disparues sans un mot.
  const terminees = useMemo(() => fenetreDemandes.filter((d) => d.type !== 'planifie' && ['maintenue', 'annulee', 'fusionnee'].includes(d.statut) && jourLocal(d.updated_at) === todayISO())
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at)), [fenetreDemandes]);

  if (!peutGerer) {
    return (
      <div className="page">
        <div className="empty-state-card">
          <div className="empty-state-title">Accès réservé</div>
          <div className="empty-state-desc">La console Urgence est réservée aux rôles de gestion (responsable, secrétariat, RH).</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-heading"><Siren className="icon" /><span className="page-title">Urgence</span></div>
        <span className="u-maj">Mis à jour à {heure(majA)} · actualisation automatique toutes les 30 s
          <button type="button" className="btn btn-ghost btn-sm" onClick={actualiserManuel}><RefreshCw className="icon-sm icon-inline" aria-hidden="true" />Actualiser</button></span>
      </div>

      {error && <div className="u-erreur mb-16" role="alert"><span>{error}</span><button type="button" className="u-lien" onClick={() => setError(null)}>Fermer</button></div>}

      <div className="u-layout">
        <div className="u-col">
          <DeclarerCarte seances={seances} ateliers={ateliers} personnes={personnes} poles={poles} demandesDeLaFenetre={fenetreDemandes}
            onDeclarer={(s) => setFenetre({ declarer: s })} onOuvrirUrgence={choisir} onAutreCas={() => setFenetre('autre')} />
          <UrgencesEnCours ouvertes={ouvertes} terminees={terminees} selectedId={selectedId} now={now} seuilMinutes={seuilMinutes} chargement={loading} onSelect={choisir} />
        </div>
        <div className="u-col">
          <SmsAEnvoyer version={versionSms} />
          {selected && selected.id === selectedId ? (
            <TableauUrgence demande={selected} atelier={selectedAtelier} contexte={contexte} contexteIndisponible={contexteIndisponible} now={now} seuilMinutes={seuilMinutes}
              confirmation={confirmation?.id === selected.id ? confirmation.prevenus : null} distant={distant} conflit={conflit} personnes={personnes} hasFra={!!fak && !!(fak.contenu || fak.lien_document)}
              onAppel={surAppel} onCorriger={surCorriger} onPasser={surPasser} onCommentaire={surCommentaire} onConfirmerRemplacement={(id) => void confirmerRemplacement(id)}
              onIssue={(k) => setFenetre(k)} onActualiser={actualiserManuel} onFra={() => setFicheFakOuverte(true)} enCours={occupe} />
          ) : (
            <section className="card">{selectedId != null || loading ? <div className="spinner" /> : <p className="u-vide">Aucune urgence choisie. Déclarez-en une à gauche, ou ouvrez-en une en cours.</p>}</section>
          )}
        </div>
      </div>

      <div className="sr-only" role="status" aria-live="polite">{annonce}</div>

      {fenetre && typeof fenetre === 'object' && (
        <ModalDeclarer atelier={atelierParId.get(fenetre.declarer.atelier_id)!} seance={fenetre.declarer} personnes={personnes} pole={poleDe(atelierParId.get(fenetre.declarer.atelier_id))}
          onClose={() => setFenetre(null)} onDone={surDeclaree} />
      )}
      {fenetre === 'autre' && <ModalAutreCas ateliers={ateliers} personnes={personnes} onClose={() => setFenetre(null)} onDone={surDeclaree} />}
      {fenetre === 'fusion' && selected && <ModalFusion demande={selected} ateliers={ateliers.filter((a) => a.id !== selected.atelier_id)} onClose={() => setFenetre(null)} onDone={surIssue} />}
      {fenetre === 'maintien' && selected && <ModalMaintien demande={selected} personnes={personnes} maintienPossible={selectedAtelier?.maintien_possible ?? false} fak={fak} onClose={() => setFenetre(null)} onDone={surIssue} />}
      {fenetre === 'annulation' && selected && <ModalAnnulation demande={selected} onClose={() => setFenetre(null)} onDone={surIssue} />}
      {ficheFakOuverte && selected && <FicheFakOverlay atelierId={selected.atelier_id} demandeId={selected.id} onClose={() => setFicheFakOuverte(false)} />}
    </div>
  );
}
